import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Partner, PartnerCommission } from '../../../domain/partners/types';
import type { PartnerCommissionRow, PartnerDashboardStats, PartnerReferralRow } from '../../../domain/partners/adminTypes';

function startOfMonthIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}

type OrgEmbed = { name: string; subscription_status: string | null };

type ReferralQueryRow = {
  id: string;
  organization_id: string;
  attributed_at: string;
  commission_ends_at: string;
  organizations: OrgEmbed | OrgEmbed[] | null;
};

function embedOrg(value: ReferralQueryRow['organizations']): OrgEmbed | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

export function usePartnerProfile() {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setPartner(null);
      setIsLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('partners')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('[usePartnerProfile]', error.message);
      setPartner(null);
    } else {
      setPartner(data as Partner | null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { partner, isLoading, refetch: load };
}

export function usePartnerDashboard(partnerId: string | undefined) {
  const [stats, setStats] = useState<PartnerDashboardStats | null>(null);
  const [referrals, setReferrals] = useState<PartnerReferralRow[]>([]);
  const [commissions, setCommissions] = useState<PartnerCommissionRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!partnerId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    const monthStart = startOfMonthIso();

    const [referralsRes, commissionsRes] = await Promise.all([
      supabase
        .from('partner_referrals')
        .select('id, organization_id, attributed_at, commission_ends_at, organizations(name, subscription_status)')
        .eq('partner_id', partnerId)
        .order('attributed_at', { ascending: false }),
      supabase
        .from('partner_commissions')
        .select('id, organization_id, stripe_invoice_id, amount_paid, commission_amount, status, available_at, created_at')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false })
        .limit(50),
    ]);

    const referralRows = (referralsRes.data ?? []) as ReferralQueryRow[];

    const mappedReferrals: PartnerReferralRow[] = referralRows.map((row) => {
      const org = embedOrg(row.organizations);
      return {
        id: row.id,
        organizationId: row.organization_id,
        organizationName: org?.name || 'Organização',
        subscriptionStatus: org?.subscription_status ?? null,
        attributedAt: row.attributed_at,
        commissionEndsAt: row.commission_ends_at,
      };
    });

    const commissionRows = (commissionsRes.data || []) as PartnerCommission[];
    const mappedCommissions: PartnerCommissionRow[] = commissionRows.map((row) => ({
      id: row.id,
      organizationId: row.organization_id,
      stripeInvoiceId: row.stripe_invoice_id,
      amountPaid: row.amount_paid,
      commissionAmount: row.commission_amount,
      status: row.status,
      availableAt: row.available_at,
      createdAt: row.created_at,
    }));

    const activeReferrals = mappedReferrals.filter(
      (r) => r.subscriptionStatus === 'active' || r.subscriptionStatus === 'trialing',
    ).length;

    const monthCommissionCents = mappedCommissions
      .filter((c) => c.createdAt >= monthStart && c.status !== 'reversed')
      .reduce((sum, c) => sum + c.commissionAmount, 0);

    const availableBalanceCents = mappedCommissions
      .filter((c) => c.status === 'available')
      .reduce((sum, c) => sum + c.commissionAmount, 0);

    const paidTotalCents = mappedCommissions
      .filter((c) => c.status === 'paid')
      .reduce((sum, c) => sum + c.commissionAmount, 0);

    setStats({
      referralCount: mappedReferrals.length,
      activeReferrals,
      monthCommissionCents,
      availableBalanceCents,
      paidTotalCents,
    });
    setReferrals(mappedReferrals);
    setCommissions(mappedCommissions);
    setIsLoading(false);
  }, [partnerId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { stats, referrals, commissions, isLoading, refetch: load };
}
